import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import BookingSection from "@/components/BookingSection";
import CityShowcase from "@/components/CityShowcase";
import {
  Scale, Gavel, Building2, FileSignature, ShieldCheck, Users, Briefcase,
  ArrowRight, Video, Phone, MapPin, Mail, Star, ChevronRight, Calendar, Menu, X, Vote,
} from "lucide-react";

const PHOTOS = {
  courthouse: "https://static.prod-images.emergentagent.com/jobs/0793a609-6c7b-485b-9fe0-b85cb68b4d17/images/5952b77eb58d8e20602e9673f6ac3996a8aea6786dfdc231f1507e0dc5fe6d57.jpeg",
  outdoor: "https://customer-assets-agu9un31.emergentagent.net/job_avvocato-turnos/artifacts/voe29fy8_WhatsApp%20Image%202026-09-20%20at%2012.22.53%20%282%29.jpeg",
  candid: "https://customer-assets-agu9un31.emergentagent.net/job_avvocato-turnos/artifacts/g04bvxjd_WhatsApp%20Image%202026-09-20%20at%2012.22.54.jpeg",
  officeArms: "https://customer-assets-agu9un31.emergentagent.net/job_avvocato-turnos/artifacts/68t19a40_WhatsApp%20Image%202026-09-20%20at%2012.22.54%20%281%29.jpeg",
  officeWindow: "https://customer-assets-agu9un31.emergentagent.net/job_avvocato-turnos/artifacts/s5vwp1d8_WhatsApp%20Image%202026-09-20%20at%2012.22.54%20%282%29.jpeg",
  campaign: "https://customer-assets-agu9un31.emergentagent.net/job_avvocato-turnos/artifacts/8so8plyv_WhatsApp%20Image%202026-09-20%20at%2013.27.34.jpeg",
};

const AREAS = [
  { icon: Building2, title: "Diritto Civile & Societario", desc: "Governance, contratti e operazioni straordinarie per imprese e privati." },
  { icon: Gavel, title: "Diritto Penale d'Impresa", desc: "Difesa e consulenza su responsabilita amministrativa e reati societari." },
  { icon: Scale, title: "Contenzioso & Arbitrati", desc: "Assistenza processuale in ogni grado di giudizio e nelle sedi arbitrali." },
  { icon: FileSignature, title: "Contrattuale & Real Estate", desc: "Redazione, negoziazione e due diligence immobiliari." },
  { icon: ShieldCheck, title: "Tutela Patrimoniale & Family Office", desc: "Protezione del patrimonio, trust e passaggi generazionali." },
  { icon: Briefcase, title: "Diritto del Lavoro", desc: "Relazioni industriali, contenzioso e gestione del personale." },
];

const STATS = [
  { value: "25+", label: "Anni di esperienza" },
  { value: "1200+", label: "Assistiti seguiti" },
  { value: "3", label: "Sedi: Napoli · Roma · Milano" },
  { value: "94%", label: "Esiti favorevoli" },
];

const TESTIMONIALS = [
  { quote: "Competenza rara e attenzione al dettaglio in ogni fase del contenzioso. Un punto di riferimento.", name: "Alessandro M.", role: "CEO, gruppo industriale" },
  { quote: "Approccio strategico e riservatezza assoluta nella tutela del patrimonio familiare.", name: "Chiara D.", role: "Family Office" },
  { quote: "Rapidita e chiarezza. La consulenza online ci ha permesso di decidere in tempi rapidi.", name: "Studio Delta S.p.A.", role: "Cliente corporate" },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const navLinks = [
    { href: "#aree", label: "Aree di Attivita" },
    { href: "#studio", label: "Lo Studio" },
    { href: "#fondatore", label: "Il Fondatore" },
    { href: "#territorio", label: "Territorio" },
    { href: "#contatti", label: "Contatti" },
  ];
  const bookBtn = (
    <Button data-testid="btn-book-consultation" className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold rounded-full px-5 sm:px-6 h-11 transition-colors">
      <span className="sm:hidden">Prenota</span>
      <span className="hidden sm:inline">Prenota Consulenza</span>
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#F4EFE4] grain-overlay">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#F4EFE4]/90 border-b border-amber-500/15">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" aria-label="Torna alla home" data-testid="nav-home-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><Brand /></Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm text-stone-600">
            <a href="#aree" className="hover:text-amber-600 transition-colors">Aree di Attivita</a>
            <a href="#studio" className="hover:text-amber-600 transition-colors">Lo Studio</a>
            <a href="#fondatore" className="hover:text-amber-600 transition-colors">Il Fondatore</a>
            <a href="#territorio" className="hover:text-amber-600 transition-colors">Territorio</a>
            <a href="#contatti" className="hover:text-amber-600 transition-colors">Contatti</a>
            <Link to="/elezioni" data-testid="nav-elezioni-link" className="hover:text-amber-600 transition-colors">Elezioni</Link>
            <Link to="/login" data-testid="nav-console-link" className="hover:text-amber-600 transition-colors">Console Staff</Link>
          </nav>
          <div className="flex items-center gap-3">
            <BookingSection trigger={bookBtn} />
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={menuOpen}
              className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full border border-amber-500/30 text-stone-800 hover:bg-amber-500/10 transition-colors"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-amber-500/15 bg-[#F4EFE4]/98 backdrop-blur-md animate-fade-up" data-testid="mobile-menu">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`mobile-nav-${l.href.slice(1)}`}
                  className="py-3 px-2 text-stone-700 hover:text-amber-600 border-b border-amber-500/10 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/elezioni"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-elezioni"
                className="py-3 px-2 text-stone-700 hover:text-amber-600 border-b border-amber-500/10 flex items-center gap-2 transition-colors"
              >
                <Vote className="w-4 h-4" /> Elezioni
              </Link>
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                data-testid="mobile-nav-console"
                className="py-3 px-2 text-amber-700 font-medium flex items-center gap-2"
              >
                <Users className="w-4 h-4" /> Console Staff
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1589216532372-1c2a367900d9?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="" className="w-full h-full object-cover opacity-80" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F4EFE4] via-[#F4EFE4]/75 to-transparent" />
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl animate-glow" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/5 px-4 py-1.5 mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs uppercase tracking-[0.25em] text-amber-600/90">Disponibilita oggi · In Studio & Online</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-stone-900 leading-[1.05] tracking-tight mb-6">
              Strategia e<br /><span className="text-amber-600">Difesa di Alto Profilo</span>
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed max-w-xl mb-10">
              Studio Legale IusFera assiste imprese e privati con un approccio sartoriale.
              Prenota un consulto in presenza presso le sedi di Napoli, Roma e Milano, oppure in videoconferenza.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <BookingSection trigger={
                <Button data-testid="hero-book-btn" className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold rounded-full px-8 h-13 py-6 text-base transition-colors">
                  <Calendar className="w-5 h-5 mr-2" /> Prenota in Presenza / Online
                </Button>
              } />
              <a href="#aree">
                <Button variant="outline" className="border-amber-500/40 text-stone-800 hover:bg-amber-500/10 hover:text-amber-700 rounded-full px-8 h-13 py-6 text-base w-full">
                  Esplora le Aree di Pratica <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Campaign — Cassa Forense */}
      <section id="campagna" className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="card-luxe rounded-3xl overflow-hidden grid md:grid-cols-2 items-stretch" data-testid="campaign-banner">
          <div className="relative bg-[#0b2a5b] flex items-center justify-center p-4">
            <img src={PHOTOS.campaign} alt="Insieme per l'Avvocatura — Lista N.1 — Cassa Forense, Distretto di Napoli 2026-2030"
              className="w-full h-full max-h-[520px] object-contain" data-testid="campaign-image" />
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#0b2a5b]/25 bg-[#0b2a5b]/5 px-4 py-1.5 mb-5 w-fit">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-700 animate-pulse" />
              <span className="text-xs uppercase tracking-[0.22em] text-[#0b2a5b] font-semibold">Elezioni Cassa Forense 2026-2030</span>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium mb-4 leading-tight">
              Insieme per l'Avvocatura — <span className="text-amber-600">Scrivi Lista N. 1</span>
            </h2>
            <p className="text-stone-600 leading-relaxed mb-4">
              L'Avv. <strong className="text-stone-900">Giuseppe Fera</strong> è candidato per l'elezione dei componenti
              del Comitato dei Delegati di Cassa Forense — Distretto di Napoli. Un impegno concreto a sostegno
              della professione forense.
            </p>
            <div className="rounded-xl bg-[#F4EFE4] border border-amber-500/20 px-5 py-4 mb-6">
              <div className="text-xs uppercase tracking-wider text-amber-600/90 font-semibold mb-1">Votazioni</div>
              <div className="text-stone-900 font-medium">Da lunedì 5 a venerdì 9 ottobre 2026</div>
            </div>
            <a href={PHOTOS.campaign} target="_blank" rel="noreferrer" className="w-fit" data-testid="campaign-cta">
              <Button className="bg-[#0b2a5b] hover:bg-[#0a2350] text-white font-semibold rounded-full px-8 h-12">
                Scopri la Lista N. 1 <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-amber-500/10 bg-[#FFFFFF]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center md:text-left">
              <div className="font-serif text-3xl md:text-4xl text-amber-600 mb-1">{s.value}</div>
              <div className="text-xs uppercase tracking-wider text-stone-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Practice areas */}
      <section id="aree" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-600/80 font-semibold mb-3">Aree di Attivita</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium">Competenze trasversali, visione dedicata</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {AREAS.map((a, i) => (
            <div key={a.title} data-testid={`area-card-${i}`}
              className="group card-luxe rounded-xl p-7 hover:border-amber-500/40 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg border border-amber-500/25 bg-[#F4EFE4] flex items-center justify-center mb-5 group-hover:border-amber-500/50 transition-colors">
                <a.icon className="w-6 h-6 text-amber-600" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl text-stone-900 mb-2">{a.title}</h3>
              <p className="text-sm text-stone-500 leading-relaxed">{a.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Studio / about */}
      <section id="studio" className="bg-[#FFFFFF] border-y border-amber-500/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <img src={PHOTOS.officeArms}
              alt="Avvocato nello studio" className="rounded-2xl border border-amber-500/20 w-full h-[420px] object-cover object-top" />
            <div className="absolute -bottom-6 -right-6 hidden md:block card-luxe rounded-xl p-5 w-48">
              <div className="font-serif text-2xl text-amber-600">Napoli · Roma · Milano</div>
              <div className="text-xs text-stone-500 mt-1">Tre sedi al servizio dei clienti</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600/80 font-semibold mb-3">Lo Studio</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium mb-6">Un metodo rigoroso, un rapporto di fiducia</h2>
            <p className="text-stone-600 leading-relaxed mb-4">
              Da oltre venticinque anni offriamo assistenza legale d'eccellenza, unendo la solidita della
              tradizione forense a strumenti digitali moderni. Ogni pratica e seguita da un team dedicato.
            </p>
            <ul className="space-y-3 mt-6">
              {["Consulenza riservata e su misura", "Team multidisciplinare di partner e associati", "Prenotazione online con conferma immediata"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-stone-600">
                  <ChevronRight className="w-4 h-4 text-amber-600" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section id="fondatore" className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative order-2 lg:order-1">
            <img src={PHOTOS.courthouse} alt="Il fondatore"
              className="rounded-2xl border border-amber-500/20 w-full h-[560px] object-cover object-top gold-glow" />
            <div className="absolute -bottom-6 -left-6 hidden md:block card-luxe rounded-xl p-5">
              <div className="font-serif text-xl text-stone-900">Avvocato Giuseppe Fera</div>
              <div className="text-xs text-amber-600/80 mt-1">Titolare dello studio</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-600/80 font-semibold mb-3">Il Fondatore</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium mb-6">Presenza in aula, vicinanza alle persone</h2>
            <p className="text-stone-600 leading-relaxed mb-6">
              Dalla difesa nelle aule di tribunale al rapporto quotidiano con i clienti: un approccio
              diretto, riservato e concreto. Ogni assistito trova un interlocutore preparato e presente
              in ogni fase del procedimento.
            </p>
            <div className="grid grid-cols-3 gap-3">
              {[PHOTOS.officeWindow, PHOTOS.outdoor, PHOTOS.candid].map((src, i) => (
                <img key={src} src={src} alt={`Momento professionale ${i + 1}`} data-testid={`founder-gallery-${i}`}
                  className="rounded-xl border border-amber-500/15 w-full h-40 object-cover object-top hover:border-amber-500/40 transition-colors" />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* City showcase — cinematic 3D */}
      <CityShowcase />

      {/* Campaign poster — under the Territorio showcase */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-8 -mt-4" data-testid="campaign-poster-section">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-600/80 font-semibold mb-3">Cassa Forense · Distretto di Napoli</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium">Insieme per l'Avvocatura — Lista N. 1</h2>
        </div>
        <div className="card-luxe rounded-3xl overflow-hidden p-3">
          <img src={PHOTOS.campaign} alt="Locandina campagna elettorale — Insieme per l'Avvocatura, Lista N.1 — Cassa Forense Distretto di Napoli 2026-2030"
            className="w-full h-auto rounded-2xl" data-testid="campaign-poster-image" />
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs uppercase tracking-[0.25em] text-amber-600/80 font-semibold mb-3">Riconoscimenti</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium">La parola ai nostri assistiti</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="card-luxe rounded-xl p-7">
              <div className="flex gap-1 mb-4">{[...Array(5)].map((_, i) => <Star key={`${t.name}-star-${i}`} className="w-4 h-4 fill-amber-500 text-amber-600" />)}</div>
              <p className="font-serif text-lg text-stone-800 italic leading-relaxed mb-6">"{t.quote}"</p>
              <div className="text-sm text-stone-900 font-medium">{t.name}</div>
              <div className="text-xs text-stone-500">{t.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl border border-amber-500/20 bg-gradient-to-br from-[#FFFFFF] to-[#F4EFE4] p-10 md:p-16 text-center">
          <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
          <div className="relative">
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 font-medium mb-4">Prenota il tuo consulto oggi</h2>
            <p className="text-stone-600 max-w-xl mx-auto mb-8">Scegli tra appuntamento in studio o videoconferenza. Riceverai una email di conferma con tutti i dettagli.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <BookingSection trigger={
                <Button data-testid="cta-book-online" className="bg-amber-500 hover:bg-amber-400 text-[#221C12] font-semibold rounded-full px-8 h-12"><Video className="w-4 h-4 mr-2" /> Consulenza Online</Button>
              } />
              <BookingSection trigger={
                <Button variant="outline" className="border-amber-500/40 text-stone-800 hover:bg-amber-500/10 rounded-full px-8 h-12"><Building2 className="w-4 h-4 mr-2" /> Appuntamento in Studio</Button>
              } />
            </div>
          </div>
        </div>
      </section>

      {/* Campaign poster — end of page */}

      {/* Footer */}
      <footer id="contatti" className="border-t border-amber-500/10 bg-[#F4EFE4]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-3 gap-10">
          <div>
            <Brand className="mb-4" />
            <p className="text-sm text-stone-500 leading-relaxed">Assistenza legale d'eccellenza per imprese e privati. Napoli, Roma e Milano.</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-stone-900 mb-4 uppercase tracking-wider">Contatti</h4>
            <ul className="space-y-3 text-sm text-stone-500">
              <li className="flex items-center gap-3"><Phone className="w-4 h-4 text-amber-600" /> 081 579 6335</li>
              <li className="flex items-center gap-3"><Mail className="w-4 h-4 text-amber-600" /> g.fera@iusfera.com</li>
              <li className="flex items-center gap-3"><MapPin className="w-4 h-4 text-amber-600" /> Napoli, Centro direzionale, Isola F11</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-stone-900 mb-4 uppercase tracking-wider">Area Riservata</h4>
            <p className="text-sm text-stone-500 mb-4">Accesso alla console operativa per avvocati e collaboratori.</p>
            <Link to="/login"><Button variant="outline" className="border-amber-500/40 text-stone-800 hover:bg-amber-500/10 rounded-full"><Users className="w-4 h-4 mr-2" /> Console Staff</Button></Link>
          </div>
        </div>
        <div className="border-t border-stone-200 py-6 text-center text-xs text-stone-500">
          © {new Date().getFullYear()} Studio Legale IusFera · P.IVA 01234567890
        </div>
      </footer>
    </div>
  );
}
